/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import useAbortController from './useAbortController';
import axiosCall from '../../services/axios';
import { useAuthContext } from '../../contexts/AuthContext';

const useUserSearch = (reqdata: any) => {
  const { get, reset } = useAbortController();
  const queryClient = useQueryClient();
  const { token } = useAuthContext();
  const queryKey = useMemo(() => ['usersearch', JSON.stringify(reqdata), token], [JSON.stringify(reqdata), token]);

  const getUsers = useCallback(async () => {
    if (!token) return { data: [] };
    try {
      const request = {
        url: `live/master/user/search`,
        method: 'post',
        data: reqdata,
      };

      const config = reset();
      const headers: Record<string, string> = {};
      if (token) headers['Session-Token'] = token;

      const resp = await axiosCall(request, config, headers);
      const data = resp?.data ?? resp;
      const users = Array.isArray(data?.Users) ? data.Users : [];

      const collator = new Intl.Collator('en', { sensitivity: 'base' });

      const sortedUsers: RepUser[] = [...users].sort((a, b) => {
        const aLast = (a?.nameLast ?? '').trim();
        const bLast = (b?.nameLast ?? '').trim();
        if (aLast !== bLast) return collator.compare(aLast, bLast);

        const aFirst = (a?.nameFirst ?? '').trim();
        const bFirst = (b?.nameFirst ?? '').trim();
        if (aFirst !== bFirst) return collator.compare(aFirst, bFirst);

        const aMiddle = (a?.nameMiddle ?? '').trim();
        const bMiddle = (b?.nameMiddle ?? '').trim();
        if (aMiddle !== bMiddle) return collator.compare(aMiddle, bMiddle);

        const aId = (a?.ID ?? '').trim();
        const bId = (b?.ID ?? '').trim();
        return collator.compare(aId, bId);
      });

      return { data: sortedUsers };
    } catch (error: any) {
      console.error(`Error fetching options: ${error?.message ?? error}`);
      return { data: [] };
    }
  }, [token, reset, reqdata]);

  // Fetch communication data using react-query
  const { data, isFetching, isLoading } = useQuery({
    queryKey: queryKey,
    queryFn: ({ signal }: { signal: AbortSignal }) => {
      return getUsers();
    },
    refetchOnWindowFocus: false,
  });

  const refetchUsers = useCallback(() => queryClient.invalidateQueries({ queryKey }), [queryClient, queryKey]);

  return {
    loadingUsers: isFetching || isLoading,
    users: data?.data ? data.data : [],
    refetchUsers,
  };
};

export default useUserSearch;
